import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { useIntl } from 'react-intl';
import { Form } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard, faPlus, faTrashCan } from '@fortawesome/free-solid-svg-icons';

import { IUserCard } from '@share/common-types';
import { addUserCard, deleteUserCard, IMyProfileState } from '@share/store/slices';
import { FunctionalRootState, RootState } from '@share/utils';
import { NULL_VALUE } from '@constants';
import { GetCreditCardImage } from '@utils';

import { ModalConfirmation } from '../../../common/modal-confirmation';
import { PaymentMethodBase } from '../../../common/payment-method-base';
import { AccountEmptyState } from '../empty-state';

import styles from './styles.module.scss';

// Same limit the legacy "Add payment" tab enforces.
const MAX_SAVED_CARDS = 5;

const EMPTY_CARD = {
  addressLine: '',
  cvv: '',
  cardNumber: '',
  cardType: null,
  city: '',
  country: 'US',
  state: '',
  expireDate: '',
  holderName: '',
  phone: '',
  zipCode: '',
} as IUserCard;

const getLastFour = (card: IUserCard): string => card.cardNumber?.slice(-4) || '';

// `expireDate` comes back as MMYY / MM/YY / MMYYYY; the month is always first, the year last.
const getExpiry = (card: IUserCard): string => {
  const value = card.expireDate || '';
  return value.length >= 4 ? `${value.substring(0, 2)}/${value.slice(-2)}` : '';
};

/** Saved cards as tiles, plus the existing card form, opened on demand instead of always on screen. */
export const AccountPaymentMethods = (): React.ReactElement => {
  const intl = useIntl();
  const dispatch = useDispatch<ThunkDispatch<RootState, undefined, Action>>();
  const myProfileStore: IMyProfileState = useSelector((state: FunctionalRootState) => state.myProfileStore);

  const { loadingCards, loading, result } = myProfileStore;
  const userCards: IUserCard[] = result?.userCards || [];

  const [isAdding, setIsAdding] = useState(false);
  const [card, setCard] = useState<IUserCard>({ ...EMPTY_CARD });
  const [formKey, setFormKey] = useState(Date.now());
  const [cardToDelete, setCardToDelete] = useState<IUserCard | null>(null);

  const [formPayment] = Form.useForm();

  const canAddMore = userCards.length < MAX_SAVED_CARDS;

  const resetForm = () => {
    setCard({ ...EMPTY_CARD });
    formPayment.resetFields();
    setFormKey(Date.now());
  };

  const handleCancelAdd = () => {
    resetForm();
    setIsAdding(false);
  };

  const handleSave = () => {
    formPayment.validateFields()
      .then(() => {
        dispatch(addUserCard(card, () => {
          resetForm();
          setIsAdding(false);
        }));
      })
      .catch((info: { errorFields?: { name: (string | number)[] }[] }) => {
        // Field errors are already shown inline; bring the first one into view.
        const firstError = info?.errorFields?.[0];
        if (firstError) {
          formPayment.scrollToField(firstError.name);
        }
      });
  };

  const handleConfirmDelete = () => {
    if (cardToDelete) {
      dispatch(deleteUserCard(cardToDelete, () => setCardToDelete(null)));
    }
  };

  const showEmpty = !userCards.length && !isAdding && !loading;

  return (
    <div className={styles.paymentMethods}>
      {showEmpty ? (
        <AccountEmptyState
          variant="panel"
          icon={faCreditCard}
          title={intl.formatMessage({ id: 'my.account.hub.payment.empty_title' })}
          body={intl.formatMessage({ id: 'my.account.hub.payment.empty_body' })}
          action={{ label: intl.formatMessage({ id: 'my.account.hub.payment.add' }), onClick: () => setIsAdding(true) }}
        />
      ) : null}

      {userCards.length ? (
        <ul className={styles.grid} aria-label={intl.formatMessage({ id: 'my.account.hub.payment.saved' })}>
          {userCards.map(savedCard => (
            <li key={savedCard.id || getLastFour(savedCard)} className={styles.tile}>
              <div className={styles.tileTop}>
                <span className={styles.brand}>{GetCreditCardImage(savedCard.cardType as string, true)}</span>
                <button
                  type="button"
                  className={styles.removeButton}
                  onClick={() => setCardToDelete(savedCard)}
                  disabled={loadingCards}
                  aria-label={intl.formatMessage({ id: 'my.account.hub.payment.remove_card' }, { last4: getLastFour(savedCard) })}
                >
                  <FontAwesomeIcon icon={faTrashCan} aria-hidden="true" />
                </button>
              </div>
              <div className={styles.number}>•••• {getLastFour(savedCard)}</div>
              <div className={styles.meta}>
                {savedCard.holderName ? <span className={styles.holder}>{savedCard.holderName}</span> : null}
                {getExpiry(savedCard) ? (
                  <span>{intl.formatMessage({ id: 'my.account.hub.payment.expires' }, { date: getExpiry(savedCard) })}</span>
                ) : null}
              </div>
            </li>
          ))}

          {canAddMore && !isAdding ? (
            <li>
              <button type="button" className={styles.addTile} onClick={() => setIsAdding(true)}>
                <span className={styles.addIcon} aria-hidden="true"><FontAwesomeIcon icon={faPlus} /></span>
                {intl.formatMessage({ id: 'my.account.hub.payment.add' })}
              </button>
            </li>
          ) : null}
        </ul>
      ) : null}

      {!canAddMore ? (
        <p className={styles.limit}>{intl.formatMessage({ id: 'available.cards.limit' })}</p>
      ) : null}

      {isAdding && canAddMore ? (
        <section className={styles.formCard} aria-labelledby="account-hub-add-card-title">
          <h2 id="account-hub-add-card-title" className={styles.formTitle}>
            {intl.formatMessage({ id: 'my.account.hub.payment.add_title' })}
          </h2>
          <div className={styles.formBody}>
            <PaymentMethodBase
              form={formPayment}
              key={formKey}
              card={card as any}
              loading={loadingCards}
              setCard={setCard}
              errorsField={NULL_VALUE}
              formRef={NULL_VALUE}
              display
              isRequired
              useFormLabel
              roundBorders
              fullWidthForm
              includePhone
              skipStorage
              disableInsurance
              hideSavedCards
            />
          </div>
          <div className={styles.formActions}>
            <button type="button" className={styles.secondaryButton} onClick={handleCancelAdd} disabled={loadingCards}>
              {intl.formatMessage({ id: 'cancel' })}
            </button>
            <button type="button" className={styles.primaryButton} onClick={handleSave} disabled={loadingCards}>
              {intl.formatMessage({ id: 'my.account.hub.payment.save' })}
            </button>
          </div>
        </section>
      ) : null}

      <ModalConfirmation
        message="confirm.delete.credit.card"
        title="delete.credit.card"
        yesMessage="confirm"
        noMessage="cancel"
        visible={!!cardToDelete}
        disabled={loadingCards}
        onCancel={() => {
          if (!loadingCards) {
            setCardToDelete(null);
          }
        }}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};
